import {
  Calculator,
  Download,
  FolderOpen,
  Lightbulb,
  Pencil,
  Pin,
  RotateCcw,
  SlidersHorizontal,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChartView } from "../../components/ChartView";
import {
  ApiError,
  scenarioApi,
  type ScenarioResult,
  type ScenarioTemplate,
  type ScenarioValueUnit,
  type WhatIfPeriod,
} from "../../services/api";
import type { PanelResult } from "../../types";
import { KpiStrip } from "./components/KpiStrip";
import { LeverControl } from "./components/LeverControl";
import {
  MAX_PINNED,
  PinnedScenarios,
  type PinnedScenario,
} from "./components/PinnedScenarios";
import { ScenarioBridge } from "./components/ScenarioBridge";
import { SensitivityGrid } from "./components/SensitivityGrid";
import {
  scenarioFileName,
  scenarioWorkbook,
  type ScenarioInputs,
} from "./scenarioFile";
import { PERIOD_LABELS, formatAmount } from "./scenarioFormat";
import { defaultLevers } from "./scenarioLayout";
import { XLSX_MIME } from "./xlsx";

/** Long enough to coalesce a slider drag, short enough to feel live. */
export const EVALUATE_DEBOUNCE_MS = 350;

const CHART_UNITS: Record<ScenarioValueUnit, string> = {
  currency: "USD",
  currency_per_hour: "USD/h",
  hours: "hours",
  percent: "percent",
  count: "count",
  months: "months",
  seconds: "seconds",
};

interface ScenarioWorkbenchProps {
  template: ScenarioTemplate;
  contextId: string;
  contextVersion?: string | null;
  pins: PinnedScenario[];
  onPinsChange: (pins: PinnedScenario[]) => void;
  /** Inputs to start from, such as a scenario opened from a file. */
  initial?: ScenarioInputs;
  /** A saved scenario file the user chose to open. */
  onOpen: (file: File) => void;
  /** Offered for the user's own custom scenarios only. */
  onEdit?: () => void;
  onDelete?: () => void;
}

function download(bytes: Uint8Array, fileName: string) {
  const url = URL.createObjectURL(new Blob([bytes], { type: XLSX_MIME }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked after the click has been handled, or some browsers cancel the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function message(error: unknown): string {
  return error instanceof ApiError
    ? error.message
    : "The scenario could not be calculated.";
}

function chartResult(
  panelId: string,
  rows: Record<string, unknown>[],
  columns: string[],
  units: Record<string, string>,
): PanelResult {
  return {
    panelId,
    status: "ok",
    columns,
    rows,
    rowCount: rows.length,
    truncated: false,
    units,
  };
}

/** One executive scenario: levers on the left, the answer on the right. */
export function ScenarioWorkbench({
  template,
  contextId,
  contextVersion,
  pins,
  onPinsChange,
  initial,
  onOpen,
  onEdit,
  onDelete,
}: ScenarioWorkbenchProps) {
  const [levers, setLevers] = useState(() => initial?.levers ?? defaultLevers(template));
  const [period, setPeriod] = useState<WhatIfPeriod>(
    initial?.period ?? template.defaultPeriod,
  );
  const [breakdown, setBreakdown] = useState(
    initial?.breakdown ?? template.breakdowns[0]?.member ?? "",
  );
  const [preset, setPreset] = useState<string | null>(() =>
    initial ? null : (template.presets.find((item) => item.id === "plan")?.id ?? null),
  );
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  // Responses can return out of order while a slider is dragged; only the
  // latest request may update the screen.
  const sequence = useRef(0);

  const drivers = template.levers.filter((item) => item.group === "driver");
  const assumptions = template.levers.filter((item) => item.group === "assumption");

  useEffect(() => {
    const current = ++sequence.current;
    setRunning(true);
    const timer = window.setTimeout(() => {
      scenarioApi
        .evaluate({
          contextId,
          contextVersion,
          templateId: template.id,
          period,
          breakdown: breakdown || null,
          levers,
        })
        .then((next) => {
          if (current !== sequence.current) return;
          setResult(next);
          setError("");
        })
        .catch((caught) => {
          if (current !== sequence.current) return;
          setError(message(caught));
        })
        .finally(() => {
          if (current === sequence.current) setRunning(false);
        });
    }, EVALUATE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [contextId, contextVersion, template.id, period, breakdown, levers]);

  const setLever = useCallback((id: string, value: number) => {
    setPreset(null);
    setLevers((previous) => ({ ...previous, [id]: value }));
  }, []);

  function applyPreset(id: string) {
    const chosen = template.presets.find((item) => item.id === id);
    if (!chosen) return;
    setPreset(id);
    setLevers((previous) => ({ ...previous, ...chosen.levers }));
  }

  function reset() {
    setLevers(defaultLevers(template));
    setPeriod(template.defaultPeriod);
    setPreset(template.presets.find((item) => item.id === "plan")?.id ?? null);
  }

  function pin() {
    if (!result || pins.length >= MAX_PINNED) return;
    const presetLabel = template.presets.find((item) => item.id === preset)?.label;
    const name = presetLabel ?? `Scenario ${String.fromCharCode(65 + pins.length)}`;
    onPinsChange([
      ...pins,
      {
        id: `${result.provenance.specHash}-${Date.now()}`,
        name,
        period: result.period,
        levers: result.levers,
        kpis: result.kpis,
        headline: result.headline,
      },
    ]);
  }

  function save() {
    const savedAt = new Date().toISOString();
    // A result still recalculating, or one that failed, describes other inputs.
    const current = !running && !error ? result : null;
    const bytes = scenarioWorkbook(
      {
        templateId: template.id,
        templateTitle: template.title,
        contextId,
        contextVersion: result?.provenance.contextVersion ?? contextVersion ?? null,
        period,
        breakdown,
        levers,
        savedAt,
      },
      template,
      current,
    );
    download(bytes, scenarioFileName(template.title, savedAt));
  }

  function restore(pinned: PinnedScenario) {
    setPreset(null);
    setPeriod(pinned.period);
    setLevers({ ...defaultLevers(template), ...pinned.levers });
  }

  const tableChart = useMemo(() => {
    const table = result?.table;
    if (!table || table.chartKeys.length === 0) return undefined;
    const series = table.columns.filter((column) => table.chartKeys.includes(column.key));
    const rows = table.rows.map((row) => ({
      label: row.label,
      ...Object.fromEntries(series.map((column) => [column.label, row[column.key]])),
    }));
    const units = Object.fromEntries(
      series.map((column) => [column.label, CHART_UNITS[column.unit]]),
    );
    return {
      encoding: { x: "label", y: series.map((column) => column.label), series: "" },
      result: chartResult(
        "scenario-breakdown",
        rows,
        ["label", ...series.map((column) => column.label)],
        units,
      ),
    };
  }, [result]);

  const trendChart = useMemo(() => {
    const trend = result?.trend;
    if (!trend || trend.points.length === 0) return undefined;
    const rows = trend.points.map((point) => ({
      label: point.label,
      Today: point.baseline,
      Scenario: point.scenario,
    }));
    const unit = CHART_UNITS[trend.unit];
    return chartResult("scenario-trend", rows, ["label", "Today", "Scenario"], {
      Today: unit,
      Scenario: unit,
    });
  }, [result]);

  return (
    <div className="scenario-layout">
      <section className="panel scenario-controls" aria-label="Scenario inputs">
        <div className="panel-heading">
          <div>
            <h2>
              <SlidersHorizontal size={15} /> {template.title}
            </h2>
            <p>{template.question}</p>
          </div>
          <div className="scenario-file-actions">
            <button
              type="button"
              className="icon-button"
              onClick={save}
              aria-label="Save scenario to Excel"
              title="Save scenario to Excel"
            >
              <Download size={15} />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => fileInput.current?.click()}
              aria-label="Open saved scenario"
              title="Open a scenario saved to Excel"
            >
              <FolderOpen size={15} />
            </button>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept={`.xlsx,${XLSX_MIME}`}
              aria-label="Saved scenario file"
              onChange={(event) => {
                const file = event.target.files?.[0];
                // Cleared so choosing the same file again still opens it.
                event.target.value = "";
                if (file) onOpen(file);
              }}
            />
            <button
              type="button"
              className="icon-button"
              onClick={reset}
              aria-label="Reset inputs"
              title="Reset inputs"
            >
              <RotateCcw size={15} />
            </button>
            {onEdit && (
              <button
                type="button"
                className="icon-button"
                onClick={onEdit}
                aria-label="Edit custom scenario"
                title="Edit this custom scenario"
              >
                <Pencil size={15} />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="icon-button"
                onClick={onDelete}
                aria-label="Delete custom scenario"
                title="Delete this custom scenario"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>

        {template.presets.length > 0 && (
          <div className="scenario-presets" role="group" aria-label="Presets">
            {template.presets.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`preset-chip${preset === item.id ? " active" : ""}`}
                aria-pressed={preset === item.id}
                title={item.description}
                onClick={() => applyPreset(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        <label>
          Baseline period
          <select
            value={period}
            onChange={(event) => setPeriod(event.target.value as WhatIfPeriod)}
          >
            {template.periods.map((item) => (
              <option key={item} value={item}>
                {PERIOD_LABELS[item]}
              </option>
            ))}
          </select>
        </label>

        {template.breakdowns.length > 0 && (
          <label>
            Break down by {!template.breakdownRequired && <small>Optional</small>}
            <select value={breakdown} onChange={(event) => setBreakdown(event.target.value)}>
              {!template.breakdownRequired && <option value="">Portfolio total</option>}
              {template.breakdowns.map((item) => (
                <option key={item.member} value={item.member}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {drivers.map((lever) => (
          <LeverControl
            key={lever.id}
            lever={lever}
            value={levers[lever.id] ?? lever.default}
            onChange={(value) => setLever(lever.id, value)}
          />
        ))}

        {assumptions.length > 0 && (
          <details className="scenario-assumption-levers" open>
            <summary>Planning assumptions</summary>
            {assumptions.map((lever) => (
              <LeverControl
                key={lever.id}
                lever={lever}
                value={levers[lever.id] ?? lever.default}
                onChange={(value) => setLever(lever.id, value)}
              />
            ))}
          </details>
        )}
      </section>

      <div
        className={`content-stack scenario-results${running ? " recalculating" : ""}`}
        aria-busy={running}
      >
        {error && (
          <div className="scenario-error" role="alert">
            <TriangleAlert size={18} />
            <span>{error}</span>
          </div>
        )}

        {!result ? (
          <section className="panel scenario-blank">
            <span className="button-spinner" />
            <h2>Reading the governed baseline…</h2>
            <p>The certified baseline is queried once, then every change is instant.</p>
          </section>
        ) : (
          <>
            <section className="panel scenario-headline">
              <div>
                <span className="status-badge simulated">Simulated</span>
                <p>{result.headline}</p>
              </div>
              <button
                type="button"
                className="button secondary"
                onClick={pin}
                disabled={pins.length >= MAX_PINNED}
                title={
                  pins.length >= MAX_PINNED
                    ? `Up to ${MAX_PINNED} scenarios can be compared`
                    : "Keep this scenario to compare"
                }
              >
                <Pin size={14} /> Pin to compare
              </button>
            </section>

            {result.warnings.map((warning) => (
              <div className="scenario-warning" role="status" key={warning}>
                <TriangleAlert size={16} />
                <span>{warning}</span>
              </div>
            ))}

            <KpiStrip kpis={result.kpis} />

            <div className="scenario-split">
              {result.bridge.length > 0 && (
                <section className="panel">
                  <div className="panel-heading">
                    <div>
                      <h2>{result.bridgeTitle ?? "Bridge"}</h2>
                      <p>From today to the scenario, step by step</p>
                    </div>
                  </div>
                  <ScenarioBridge
                    steps={result.bridge}
                    unit={result.bridgeUnit ?? "currency"}
                    title={result.bridgeTitle ?? "Bridge"}
                  />
                </section>
              )}
              {result.insights.length > 0 && (
                <section className="panel scenario-insights">
                  <div className="panel-heading">
                    <div>
                      <h2>
                        <Lightbulb size={15} /> What this tells you
                      </h2>
                      <p>Calculated from the scenario, not generated</p>
                    </div>
                  </div>
                  <ul>
                    {result.insights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {pins.length > 0 && (
              <section className="panel">
                <div className="panel-heading">
                  <div>
                    <h2>Scenario comparison</h2>
                    <p>Pinned scenarios against the one you are editing</p>
                  </div>
                </div>
                <PinnedScenarios
                  pins={pins}
                  current={result}
                  onRemove={(id) => onPinsChange(pins.filter((item) => item.id !== id))}
                  onRestore={restore}
                />
              </section>
            )}

            {result.table && (
              <section className="panel scenario-comparison">
                <div className="panel-heading">
                  <div>
                    <h2>{result.table.title}</h2>
                    <p>
                      {result.table.truncated
                        ? "Largest groups shown"
                        : "Today compared with the scenario"}
                    </p>
                  </div>
                  <span className="status-badge simulated">Simulated</span>
                </div>
                {tableChart && (
                  <ChartView
                    panelId="scenario-breakdown"
                    chartType="bar"
                    encoding={tableChart.encoding}
                    result={tableChart.result}
                  />
                )}
                <div className="panel-table-wrap">
                  <table className="panel-table">
                    <thead>
                      <tr>
                        <th scope="col">{result.table.dimensionLabel}</th>
                        {result.table.columns.map((column) => (
                          <th scope="col" className="numeric" key={column.key}>
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.table.rows.map((row, index) => (
                        <tr key={`${String(row.label)}-${index}`}>
                          <td>{String(row.label ?? "Not set")}</td>
                          {result.table?.columns.map((column) => (
                            <td className="numeric" key={column.key}>
                              {formatAmount(
                                typeof row[column.key] === "number"
                                  ? (row[column.key] as number)
                                  : null,
                                column.unit,
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {trendChart && result.trend && (
              <section className="panel scenario-comparison">
                <div className="panel-heading">
                  <div>
                    <h2>{result.trend.title}</h2>
                    <p>Today compared with the scenario</p>
                  </div>
                </div>
                <ChartView
                  panelId="scenario-trend"
                  chartType="line"
                  encoding={{ x: "label", y: ["Today", "Scenario"], series: "" }}
                  result={trendChart}
                />
              </section>
            )}

            {result.sensitivity && (
              <section className="panel">
                <div className="panel-heading">
                  <div>
                    <h2>{result.sensitivity.title}</h2>
                    <p>How far the answer moves if the inputs are wrong</p>
                  </div>
                </div>
                <SensitivityGrid grid={result.sensitivity} />
              </section>
            )}

            <section className="panel scenario-notes">
              <div>
                <Calculator size={18} />
                <div>
                  <h2>Assumptions</h2>
                  <ul>
                    {result.assumptions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="scenario-disclaimer">{result.disclaimer}</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
